import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FormErrorAlert } from "@/components/stone/form-errors";
import { Button } from "@/components/ui/button";
import { getErrorMessage } from "@/lib/form-errors";
import { encodePlusCode, tryDecodePlusCode } from "@/lib/plus-codes";
import { cn } from "@/lib/utils";
import { trpc } from "@/utils/trpc";
import {
  AddressField,
  GeofenceDistanceField,
  LocationMapField,
  SiteNameField,
} from "./site-setup-fields";
import {
  type SiteSetupFormApi,
  type SiteSetupProps,
  siteSetupFormSchema,
} from "./site-setup-types";

function SiteSetupForm({
  form,
  formId,
  isEdit,
  onCancel,
  showActions = true,
  geofenceHelperText,
  submitError,
  onClearSubmitError,
}: {
  form: SiteSetupFormApi;
  formId?: string;
  isEdit: boolean;
  onCancel?: () => void;
  showActions?: boolean;
  geofenceHelperText?: string;
  submitError?: string | null;
  onClearSubmitError?: () => void;
}) {
  const handleLocationChange = useCallback(() => {
    // setIsManualPlusCodeEditing(false);
    onClearSubmitError?.();
  }, [onClearSubmitError]);

  const latValue = form.getFieldValue("lat");
  const lonValue = form.getFieldValue("lon");
  const plusCodeValue = form.getFieldValue("plusCode");

  useEffect(() => {
    if (
      typeof latValue === "number" &&
      Number.isFinite(latValue) &&
      typeof lonValue === "number" &&
      Number.isFinite(lonValue)
    ) {
      const decoded = tryDecodePlusCode(plusCodeValue);
      const matchesCurrentCoordinates =
        decoded != null &&
        Math.abs(decoded.latitudeCenter - latValue) < 1e-7 &&
        Math.abs(decoded.longitudeCenter - lonValue) < 1e-7;
      if (!matchesCurrentCoordinates) {
        const next = encodePlusCode(latValue, lonValue);
        if (next !== plusCodeValue) {
          form.setFieldValue("plusCode", next);
        }
      }
    }
  }, [form, latValue, lonValue, plusCodeValue]);

  return (
    <form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="space-y-4"
    >
      <div
        className={cn(
          "space-y-4",
          "lg:grid lg:grid-cols-[minmax(0,0.95fr)_minmax(22rem,1.05fr)] lg:items-start lg:gap-6 lg:space-y-0"
        )}
      >
        <div className="space-y-4">
          <SiteNameField form={form} onFieldInteraction={onClearSubmitError} />
          <GeofenceDistanceField
            form={form}
            helperText={geofenceHelperText}
            onFieldInteraction={onClearSubmitError}
          />
          <AddressField
            form={form}
            onLocationSelect={handleLocationChange}
            onFieldInteraction={onClearSubmitError}
          />
        </div>
        <div>
          <LocationMapField
            form={form}
            onLocationChange={handleLocationChange}
            onFieldInteraction={onClearSubmitError}
          />
        </div>
      </div>
      <FormErrorAlert message={submitError} />
      {showActions ? (
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, isSubmitting]) => (
            <div className="flex flex-col gap-2 lg:flex-row lg:justify-end">
              {isEdit && onCancel ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full lg:w-auto"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              ) : null}
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="w-full lg:w-auto"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEdit ? "Updating..." : "Creating..."}
                  </>
                ) : isEdit ? (
                  "Update Site"
                ) : (
                  "Add Site"
                )}
              </Button>
            </div>
          )}
        </form.Subscribe>
      ) : null}
    </form>
  );
}

export default function SiteSetup({
  onComplete,
  onCancel,
  site,
  className,
  formId,
  showActions = true,
  geofenceHelperText,
}: SiteSetupProps) {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const createSiteMutation = useMutation({
    ...trpc.createSite.mutationOptions(),
    onSuccess: async (result) => {
      setSubmitError(null);
      toast.success("Site created successfully!", {
        description:
          "Sites start inactive. Activate it from the site settings when you have an available slot.",
      });
      if (onComplete) {
        await onComplete(result.site);
        return;
      }
      navigate("/sites");
    },
    onError: (error) => {
      const message = getErrorMessage(
        error,
        "Failed to create site. Please try again."
      );
      setSubmitError(message);
      toast.error(message);
      console.error("Create site error:", error);
    },
  });

  const updateSiteMutation = useMutation({
    ...trpc.updateSite.mutationOptions(),
    onSuccess: async (updatedSite) => {
      setSubmitError(null);
      toast.success("Site updated successfully!");
      await onComplete?.(updatedSite);
    },
    onError: (error) => {
      const message = getErrorMessage(
        error,
        "Failed to update site. Please try again."
      );
      setSubmitError(message);
      toast.error(message);
      console.error("Update site error:", error);
    },
  });

  const form = useForm({
    defaultValues: {
      siteName: site?.name || "",
      address: site?.address || "",
      geofenceDistance:
        site?.geofenceDistance != null ? String(site.geofenceDistance) : "100",
      lat: site?.lat ?? null,
      lon: site?.lon ?? null,
      plusCode: site?.plusCode ?? "",
    },
    validators: {
      onSubmit: siteSetupFormSchema,
    },
    onSubmit: async ({ value }) => {
      setSubmitError(null);
      const normalizedPlusCode = value.plusCode.trim();
      const trimmedSiteName = value.siteName.trim();
      const trimmedAddress = value.address.trim();
      if (site) {
        const addressChanged =
          trimmedAddress && trimmedAddress !== (site.address || "");
        await updateSiteMutation.mutateAsync({
          id: site.id,
          name: trimmedSiteName,
          geofenceDistance: parseInt(value.geofenceDistance, 10),
          ...(addressChanged ? { address: trimmedAddress } : {}),
          ...(Number.isFinite(value.lat) && Number.isFinite(value.lon)
            ? { lat: value.lat as number, lon: value.lon as number }
            : {}),
          ...(normalizedPlusCode ? { plusCode: normalizedPlusCode } : {}),
        });
      } else {
        await createSiteMutation.mutateAsync({
          name: trimmedSiteName,
          address: trimmedAddress,
          geofenceDistance: parseInt(value.geofenceDistance, 10),
          ...(Number.isFinite(value.lat) && Number.isFinite(value.lon)
            ? { lat: value.lat as number, lon: value.lon as number }
            : {}),
          ...(normalizedPlusCode ? { plusCode: normalizedPlusCode } : {}),
        });
      }
    },
  });

  return (
    <div
      className={cn("rounded-md border border-border bg-card p-4", className)}
    >
      <SiteSetupForm
        form={form}
        formId={formId}
        isEdit={Boolean(site)}
        onCancel={site && onCancel ? onCancel : undefined}
        showActions={showActions}
        geofenceHelperText={geofenceHelperText}
        submitError={submitError}
        onClearSubmitError={() => setSubmitError(null)}
      />
    </div>
  );
}
